import type { FAQItem } from '../seo/FAQSchema';

/** Amazon FBA removal order fee bands (illustrative — verify in Seller Central before submitting). */
export const AMAZON_REMOVAL_FEE_ROWS = [
  {
    band: 'Small standard',
    amazonFee: '£0.35–£0.52 per unit*',
    notes: 'Fees vary by product dimensions and weight tier.',
  },
  {
    band: 'Medium standard',
    amazonFee: '£0.56–£0.78 per unit*',
    notes: 'Check the fee preview in Seller Central before confirming removal.',
  },
  {
    band: 'Large Standard & Oversize',
    amazonFee: 'From £0.90 per unit*',
    notes: 'Larger products may incur significantly higher removal charges.',
  },
  {
    band: 'Disposal (Amazon)',
    amazonFee: 'Per-unit disposal fee*',
    notes: 'Compare disposal costs against removal and potential resale value.',
  },
] as const;

/** Pick Pack Pro handling fees when removal stock arrives at our MK warehouse. */
export const PPP_REMOVAL_HANDLING_ROWS = [
  { service: 'Goods-in receive & count', fee: 'Included in returns intake' },
  { service: 'Inspection & condition grade', fee: 'From £0.50 / unit' },
  { service: 'Re-prep (FNSKU, polybag, carton)', fee: 'From £0.30 / unit — see FBA prep pricing' },
  { service: 'Restock to SFP / multi-channel', fee: 'Pick & pack from £0.99 / order' },
  { service: 'Quarantine / dispose on instruction', fee: 'Quoted per batch' },
] as const;

export const INVENTORY_DISPOSAL_OPTIONS = [
  {
    option: 'Amazon Removal Order',
    bestFor: 'Recovering inventory for resale, re-prep, or SFP fulfilment',
    keyConsiderations: 'Gives you control of stock but includes Amazon removal fees.',
  },
  {
    option: 'Amazon Liquidation',
    bestFor: 'Slow-moving inventory with limited resale potential',
    keyConsiderations: 'Recover part of the product value without handling inventory yourself.',
  },
  {
    option: 'Continue Amazon Storage',
    bestFor: 'Products expected to sell soon',
    keyConsiderations: 'Ongoing storage fees may exceed the value of holding slow-moving stock.',
  },
] as const;

export const REMOVAL_ORDER_FAQS: FAQItem[] = [
  {
    question: 'What is an Amazon removal order?',
    answer:
      'An Amazon removal order is a Seller Central request to move inventory out of FBA fulfilment centres — either returned to an address you specify or disposed of by Amazon. It is different from a customer return: removals are seller-initiated and billed per Amazon’s removal fee schedule. Pick Pack Pro handles removal order arrivals at our Milton Keynes warehouse for inspection, re-prep and restock.',
  },
  {
    question: 'How much do Amazon removal orders cost in the UK?',
    answer:
      'Amazon charges per-unit removal fees based on size band (often roughly £0.35–£0.90+ per unit — verify in Seller Central on the day you submit). Disposal has its own fee table. After stock arrives, Pick Pack Pro handling (receive, inspect, re-prep) is itemised separately — typically from £0.50/unit inspection and from £0.30/unit FBA re-prep. We quote before processing large batches.',
  },
  {
    question: 'How long do Amazon removal orders take?',
    answer:
      'UK sellers commonly see 7–21 days end-to-end under normal conditions. Q4 and FC congestion can extend timelines. Once cartons arrive at Pick Pack Pro, we receive and update inventory within 24 hours and can restock sellable units the same day subject to your rules.',
  },
  {
    question: 'What is the difference between removal orders and customer returns?',
    answer:
      'Customer returns are buyer-initiated parcels coming back from end consumers. Amazon removal orders are seller-initiated outbound from FBA — often whole cartons of slow-moving, stranded or unfulfillable stock. Pick Pack Pro processes both, but removal orders usually need manifest matching, pallet handling and re-prep before stock re-enters your channels.',
  },
  {
    question: 'Does Pick Pack Pro handle Amazon removal orders?',
    answer:
      'Yes. Pick Pack Pro regularly processes FBA removal orders for UK sellers. We receive Amazon removal shipments, count and inspect units, then restock for Seller Fulfilled Prime, re-prep for FBA inbound, hold in storage, or dispose per your written instruction.',
  },
];
